import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { ServiceSelector } from "@/components/ServiceSelector";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { PriceCard } from "@/components/PriceCard";
import { OrderCard } from "@/components/OrderCard";
import { AVAILABILITY } from "@/lib/constants";
import { toast, Toaster } from "sonner";
import { PackagePlus, History as HistoryIcon, User, Loader2, Send, Store } from "lucide-react";

export default function CustomerApp() {
  const { user, setUser } = useAuth();
  const [tab, setTab] = useState("order");
  const [driver, setDriver] = useState({ availability: "hors_ligne", whatsapp_number: "" });

  const loadDriver = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/status");
      setDriver(data);
    } catch (e) {
      console.error("Driver status fetch failed:", e);
    }
  }, []);

  useEffect(() => {
    loadDriver();
    const t = setInterval(loadDriver, 10000);
    return () => clearInterval(t);
  }, [loadDriver]);

  const av = AVAILABILITY[driver.availability];

  return (
    <div className="min-h-screen bg-[#0c111c] pb-24">
      <Toaster position="top-center" theme="dark" richColors />
      <Header
        right={
          <span className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${av.ring} ${av.color}`}>
            <span className={`h-2 w-2 rounded-full ${av.dot} ${driver.availability === "disponible" ? "radar" : ""}`} />
            Livreur {av.label}
          </span>
        }
      />

      <main className="mx-auto max-w-3xl px-4 py-5">
        {tab === "order" && <OrderTab user={user} driver={driver} onOrdered={() => setTab("history")} />}
        {tab === "history" && <HistoryTab driver={driver} />}
        {tab === "profile" && <ProfileTab user={user} setUser={setUser} />}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#1F293D] bg-[#0c111c]/95 backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl">
          {[
            { k: "order", label: "Commander", icon: PackagePlus },
            { k: "history", label: "Historique", icon: HistoryIcon },
            { k: "profile", label: "Profil", icon: User },
          ].map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                data-testid={`tab-${t.k}`}
                onClick={() => setTab(t.k)}
                className={`flex flex-1 flex-col items-center gap-1 py-3 text-xs font-semibold transition-colors ${
                  tab === t.k ? "text-[#ff6a00]" : "text-slate-500"
                }`}
              >
                <Icon className="h-5 w-5" />
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

function OrderTab({ user, driver, onOrdered }) {
  const isRestaurant = user.role === "restaurant";
  const [service, setService] = useState("COURSE");
  const [from, setFrom] = useState(
    isRestaurant && user.default_lat
      ? { label: user.default_address, lat: user.default_lat, lon: user.default_lon }
      : null
  );
  const [to, setTo] = useState(null);
  const [contactPhone, setContactPhone] = useState(user.phone || "");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [weight, setWeight] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!from?.lat || !to?.lat) {
      setQuote(null);
      return;
    }
    let cancel = false;
    setQuoting(true);
    (async () => {
      try {
        const { data } = await api.post("/pricing/quote", {
          service, from_lat: from.lat, from_lon: from.lon, to_lat: to.lat, to_lon: to.lon,
          weight_kg: parseFloat(weight) || 0,
        });
        if (!cancel) setQuote(data);
      } catch {
        if (!cancel) setQuote(null);
      } finally {
        if (!cancel) setQuoting(false);
      }
    })();
    return () => { cancel = true; };
  }, [service, from, to, weight]);

  const available = driver.availability === "disponible";

  const submit = async () => {
    if (!from?.lat || !to?.lat || !quote) return;
    setSubmitting(true);
    try {
      await api.post("/orders", {
        service,
        from_address: from.label, from_lat: from.lat, from_lon: from.lon,
        to_address: to.label, to_lat: to.lat, to_lon: to.lon,
        contact_phone: contactPhone, recipient_phone: recipientPhone, notes,
        weight_kg: parseFloat(weight) || 0,
      });
      toast.success("Demande envoyée au livreur !");
      onOrdered();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {!available && (
        <div data-testid="unavailable-banner" className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-300">
          Le livreur est actuellement <b>{AVAILABILITY[driver.availability].label.toLowerCase()}</b>. Vous pourrez envoyer votre demande dès qu'il sera disponible.
        </div>
      )}

      <section>
        <h2 className="mb-3 font-display text-xl font-bold text-white">1 · Type de service</h2>
        <ServiceSelector value={service} onChange={setService} />
      </section>

      <section className="space-y-4">
        <h2 className="font-display text-xl font-bold text-white">2 · Adresses</h2>
        {isRestaurant && from?.lat ? (
          <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-3">
            <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-400">
              <Store className="h-3.5 w-3.5" /> Enlèvement (votre restaurant)
            </div>
            <div className="text-sm text-slate-200">{from.label}</div>
            <button onClick={() => setFrom(null)} className="mt-1 text-xs text-[#ff6a00] underline">
              Utiliser une autre adresse
            </button>
          </div>
        ) : (
          <AddressAutocomplete
            label="Enlèvement (FROM)"
            testId="order-from-input"
            value={from}
            onSelect={setFrom}
            accent="#10B981"
          />
        )}
        <AddressAutocomplete label="Livraison (TO)" testId="order-to-input" value={to} onSelect={setTo} />
        {service === "MARCHANDISE" && (
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-400">
              Poids estimé (kg)
            </label>
            <input
              data-testid="weight-input"
              type="number"
              min="0"
              step="0.5"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="Ex : 8"
              className="w-full rounded-xl border border-[#1F293D] bg-[#151b2b] px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-[#ff6a00] focus:outline-none"
            />
            <p className="mt-1 text-xs text-slate-500">Le tarif MARCHANDISE dépend du poids et de la distance.</p>
          </div>
        )}
      </section>

      {(quoting || quote) && (
        <section>
          <h2 className="mb-3 font-display text-xl font-bold text-white">3 · Tarif</h2>
          <PriceCard quote={quote} loading={quoting} />
        </section>
      )}

      <section className="space-y-3">
        <h2 className="font-display text-xl font-bold text-white">4 · Contacts & détails</h2>
        <Input testId="contact-phone-input" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="Votre téléphone (obligatoire, ex: 33612345678)" />
        {!contactPhone.trim() && (
          <p className="text-xs text-amber-400">Votre numéro est requis pour que le livreur puisse vous appeler.</p>
        )}
        <Input testId="recipient-phone-input" value={recipientPhone} onChange={(e) => setRecipientPhone(e.target.value)} placeholder="Téléphone du destinataire (optionnel)" />
        <textarea
          data-testid="notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Notes (contenu du colis, étage, code…)"
          rows={2}
          className="w-full rounded-xl border border-[#1F293D] bg-[#151b2b] px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-[#ff6a00] focus:outline-none"
        />
      </section>

      <button
        data-testid="submit-order-btn"
        onClick={submit}
        disabled={!quote || submitting || !available || !contactPhone.trim() || (service === "MARCHANDISE" && !(parseFloat(weight) > 0))}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff6a00] py-4 font-display text-base font-bold text-white shadow-[0_0_30px_rgba(255,106,0,0.35)] transition-all hover:bg-[#e85f00] disabled:cursor-not-allowed disabled:opacity-40"
      >
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        {available ? "Envoyer la demande" : "Livreur indisponible"}
      </button>
    </div>
  );
}

function HistoryTab({ driver }) {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  }, []);

  if (orders === null)
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#ff6a00]" /></div>;

  if (!orders.length)
    return (
      <div data-testid="history-empty" className="rounded-2xl border border-[#1F293D] bg-[#151b2b] p-10 text-center text-sm text-slate-400">
        Aucune commande pour l'instant.
      </div>
    );

  return (
    <div className="space-y-4">
      <h2 className="font-display text-xl font-bold text-white">Mes commandes</h2>
      {orders.map((o) => (
        <OrderCard key={o.id} order={o} driverNumber={driver.whatsapp_number} />
      ))}
    </div>
  );
}

function ProfileTab({ user, setUser }) {
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "", default_address: user.default_address || "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Toaster position="top-center" theme="dark" richColors />
      <h2 className="font-display text-xl font-bold text-white">Mon profil</h2>
      <div className="rounded-2xl border border-[#1F293D] bg-[#151b2b] p-4">
        <div className="mb-3 text-sm text-slate-400">
          {user.email} · <span className="text-[#ff6a00]">{user.role === "restaurant" ? "Restaurant" : "Client"}</span>
        </div>
        <div className="space-y-3">
          <Input testId="profile-name" value={form.name} onChange={set("name")} placeholder="Nom / Enseigne" />
          <Input testId="profile-phone" value={form.phone} onChange={set("phone")} placeholder="Téléphone WhatsApp (ex: 33612345678)" />
          <Input testId="profile-address" value={form.default_address} onChange={set("default_address")} placeholder="Adresse par défaut" />
          <button
            data-testid="save-profile-btn"
            onClick={save}
            disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#ff6a00] py-3 font-bold text-white transition-colors hover:bg-[#e85f00] disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}

const Input = ({ testId, ...props }) => (
  <input
    data-testid={testId}
    {...props}
    className="w-full rounded-xl border border-[#1F293D] bg-[#151b2b] px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-[#ff6a00] focus:outline-none"
  />
);
